import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import "./index.css";
import usePreviewZoom from "../../hooks/usePreviewZoom.js";
import { ObjectToHtml } from "../../helpers/formaterBanner.js";
import {
  BannerContext,
  BannerDimensionsContext,
} from "../../contexts/BannerDimensionsContext.js";

const Preview = (props) => {
  const { b64 } = props;
  /**
   * @description topLevel function to be call in useState default Scale
   * @returns {number}
   */
  const getDefaultZoomValue = useCallback(() => {
    try {
      const bannerHelper = { ...props };
      if (!bannerHelper) {
        return 100;
      }
      const { width, height } = bannerHelper;

      return width && height > 700 ? 40 : 100;
    } catch {
      return 100;
    }
  }, [props]);

  const [scale, setScale] = useState(1);
  const [scaleInput, setScaleInput] = useState(getDefaultZoomValue());
  const [previousWidth, setPreviousWidth] = useState(0);
  const [previousHeight, setPreviousHeight] = useState(0);
  const refIframe = useRef();
  const refIframeContainer = useRef();
  const prevRef = useRef();
  const { previewZoom, changePreviewZoom } = usePreviewZoom();
  const { editGrid, customGrid, setCustomGrid, previewZoomToSteps } =
    useContext(BannerContext);

  const loadIframe = useCallback(() => {
    const blob = b64 ? false : true;
    const html = ObjectToHtml(props, blob);

    if (html === prevRef.current) return;

    prevRef.current = html;

    if (refIframe && refIframe.current) {
      refIframe.current.style.width = props.width + "px";
      refIframe.current.style.height = props.height + "px";
      const iframe = refIframe.current.contentDocument;

      if (!iframe) return;

      iframe.open();
      iframe.write(html);
      iframe.close();
    }
  }, [props]);

  const reloadIframe = useCallback(
    (ref) => {
      if (ref && ref.current) {
        ref.current.src = "about:blank";

        // setTimeout used to prevent the preview load two preview in same time when reloading
        setTimeout(() => {
          const html = ObjectToHtml(props, b64 ? false : true);
          const iframeDoc = ref.current.contentDocument;

          if (!iframeDoc) return;

          iframeDoc.open();
          iframeDoc.write(html);
          iframeDoc.close();
        }, 20);
      }
    },
    [props]
  );

  useEffect(() => {
    requestAnimationFrame(() => {
      loadIframe();
    });

    let zoom = getDefaultZoomValue();
    const scale = zoom / 100;
    setScale(scale);
    changePreviewZoom(0);

    if (props.height > 600) {
      const diffTop = (props.height * scale - props.height) / 2;
      const diffBottom = (props.height * scale - props.height) / 3;
      refIframeContainer.current.style.marginTop = `${diffTop}px`;
      refIframeContainer.current.style.marginBottom = `${diffBottom}px`;
    }
  }, []);

  useEffect(() => {
    if (refIframe.current) {
      requestAnimationFrame(() => {
        loadIframe();
      });
    }
  }, [loadIframe]);

  useEffect(() => {
    if (
      props.width > 750 &&
      props.height > 750 &&
      props.type === "master" && 
      props.mirror === true &&
      previousWidth !== Number(props.width) &&
      previousHeight !== Number(props.height)
    ) {
      setScale(0.4);
      setScaleInput(40);
    } else if (
      props.width < 750 &&
      props.height < 750 && 
      props.mirror === true &&
      props.type === "master" &&
      previousWidth !== Number(props.width) &&
      previousHeight !== Number(props.height)
    ) {
      setScale(0.8);
      setScaleInput(80);
    }

    if (refIframe.current) {
      requestAnimationFrame(() => {
        reloadIframe();
      });
    }

    setPreviousWidth(Number(props.width));
    setPreviousHeight(Number(props.height));
  }, [reloadIframe, props, previousWidth, previousHeight]);
  /**
   * @param {number} scale
   * @returns void
   */
  const scaleCustomLineGrid = (scale) => {
    if (customGrid.length && editGrid && previewZoomToSteps) {
      let helpX = [];
      let helpY = [];

      if (customGrid[0].x.length) {
        helpX = [...customGrid[0].x];
      }

      if (customGrid[0].y.length) {
        helpY = [...customGrid[0].y];
      }

      if (previewZoom < scale) {
        const diffScale = (scale - previewZoom) / 1.85 + 1;

        const resultX = helpX.map((item) => item * diffScale);
        const resultY = helpY.map((item) => item * diffScale);

        setCustomGrid([
          {
            ...customGrid[0],
            x: [...resultX],
            y: [...resultY],
          },
        ]);
      } else if (previewZoom > scale) {
        let diffScale = (previewZoom - scale) / 1.85 + 1;

        const resultX = helpX.map((item) => item / diffScale);
        const resultY = helpY.map((item) => item / diffScale);

        setCustomGrid([
          {
            ...customGrid[0],
            x: [...resultX],
            y: [...resultY],
          },
        ]);
      }
    }
  };

  const changeZoom = (zoomValue) => {
    const iframeContainer = refIframeContainer.current;
    if (!iframeContainer) return;

    const scale = zoomValue / 100;
    setScaleInput(zoomValue);
    setScale(scale);

    scaleCustomLineGrid(scale);

    const height = Number(props.height);
    const width = Number(props.width);
    const scaledHeight = height * scale;
    const heightDiff = scaledHeight - height;

    changePreviewZoom(scale);

    // !!! Important !!!
    // Lines below is used to calc the margin when preview is scaling by transform scale

    // Mobile Calc only
    if (props.width >= 300 && 
      props.width <= 320 && 
      props.height >= 50 && 
      props.height <= 100 && 
      props.type === "previewReplica") {
        iframeContainer.style.marginTop = `${scaledHeight}px`;
        return;
    }

    // For other resolutions:
    if (width > height) {
      if (width > 600) {
        const margin = heightDiff / 2;
        iframeContainer.style.marginTop = `${margin}px`;
        iframeContainer.style.marginBottom = `${margin}px`;
        return;
      }

      const helper = Number(props.height) - Number(props.width);

      if (helper <= 300) {
        const diffTop = (props.height * scale - props.height) / 2;
        const diffBottom = (props.height * scale - props.height) / 2;
        iframeContainer.style.marginTop = `${diffTop}px`;
        iframeContainer.style.marginBottom = `${diffBottom}px`;
        return;
      }

      const diffTop = heightDiff + 10;
      const diffBottom = heightDiff / 2;
      iframeContainer.style.marginTop = `${diffTop}px`;
      iframeContainer.style.marginBottom = `${diffBottom}px`;
      return;
    }

    if (height <= 600) {
      const marginTop = scaledHeight / 4.5;
      const marginBottom = scaledHeight / 3;
      iframeContainer.style.marginTop = `${marginTop}px`;
      iframeContainer.style.marginBottom = `${marginBottom}px`;
      return;
    }

    const marginTop = heightDiff / 2;
    const marginBottom = heightDiff / 3;
    iframeContainer.style.marginTop = `${marginTop}px`;
    iframeContainer.style.marginBottom = `${marginBottom}px`;
  };

  return (
    <BannerDimensionsContext>
      {props === null ? (
        <></>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div className="root-preview">
            {!props.mirror ? (
              <div className="containerPreviewButtonandZoom2">
                <div className="containerPreviewButton">
                  <svg
                    className="replayPreviewIframe"
                    onClick={() => reloadIframe(refIframe)}
                    id="ico_play"
                    style={{
                      display: props.estatico ? "none" : "flex",
                      cursor: "pointer",
                      marginLeft: "0px",
                    }}
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    width="38"
                    height="38"
                    viewBox="0 0 38 38"
                  >
                    <defs>
                      <clipPath id="clipPath">
                        <rect
                          id="Retângulo_44"
                          data-name="Retângulo 44"
                          width="11.718"
                          height="17.411"
                          fill="#fff"
                        />
                      </clipPath>
                    </defs>
                    <circle
                      id="Elipse_24"
                      data-name="Elipse 24"
                      cx="19"
                      cy="19"
                      r="19"
                      fill="#6e4590"
                    />
                    <g
                      id="Grupo_113"
                      data-name="Grupo 113"
                      transform="translate(15.141 10.589)"
                      clipPath="url(#clipPath)"
                    >
                      <path
                        id="Caminho_152"
                        data-name="Caminho 152"
                        d="M0,1.93V15.481c0,1.717,2.288,2.576,3.626,1.363l7.47-6.776a1.8,1.8,0,0,0,0-2.724L3.626.568C2.288-.645,0,.214,0,1.93"
                        transform="translate(0 0)"
                        fill="#fff"
                      />
                    </g>
                  </svg>
                </div>
                <div className="containerZoom">
                  <svg
                    style={{ margin: "-4px 5px 0 0" }}
                    id="ico_lupa"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                    width="16.87"
                    height="16.871"
                    viewBox="0 0 16.87 16.871"
                  >
                    <defs>
                      <clipPath id="clip-path">
                        <rect
                          id="Retângulo_37"
                          dataname="Retângulo 37"
                          width="16.87"
                          height="16.871"
                          transform="translate(0 0)"
                          fill="#1b9ada"
                        />
                      </clipPath>
                    </defs>
                    <g
                      id="Grupo_95"
                      dataname="Grupo 95"
                      transform="translate(0 0)"
                      clipPath="url(#clip-path)"
                    >
                      <path
                        id="Caminho_155"
                        dataname="Caminho 155"
                        d="M1.658,16.586l4.19-4.192a6.9,6.9,0,1,0-1.371-1.372L.285,15.213a.971.971,0,0,0,1.373,1.373m4.823-13.2a4.95,4.95,0,1,1,0,7,4.956,4.956,0,0,1,0-7"
                        fill="#1b9ada"
                      />
                    </g>
                  </svg>
                  <Box sx={{ width: 120, margin: "10px 0 0 0" }}>
                    <input
                      type="range"
                      className="sliderRange"
                      defaultValue={getDefaultZoomValue}
                      min={10}
                      max={props.height < 400 ? 250 : 150}
                      onChange={(event) => changeZoom(event.target.value)}
                      value={scaleInput}
                      id={`slider-${props.width}x${props.height}-${props.id}`}
                      name={`slider-${props.width}x${props.height}-${props.id}`}
                    />
                  </Box>
                </div>
              </div>
            ) : (
              <></>
            )}
            <div
              ref={refIframeContainer}
              className="container-preview"
              style={{
                flexDirection: props.type === "master" ? "column" : "row",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transform: `scale(${scale}) translateY(${
                  (1 - scale) * 100 * 0.4
                }px) translateX(0%)`,
                transformOrigin:
                  !props.mirror && scale > 0.9 ? "top" : "center",
                paddingLeft: "30px",
                width:
                  props.type === "master" &&
                  props.mirror &&
                  props.width > 750 &&
                  props.height > 750
                    ? "500px"
                    : "auto",
              }}
              id={"preview-parent-" + props.width + "x" + props.height}
            >
              <iframe
                key={`iframe${props.mirror}-${props.id}-${props.width}x${props.height}`}
                id={`iframe${props.mirror}-${props.id}-${props.width}x${props.height}`}
                style={{
                  backgroundColor: props.backgroundColor || "#fff",
                  width: props.width + "px",
                  height: props.height + "px",
                  border: 0,
                }}
                ref={refIframe}
                title="iframePreview"
              ></iframe>
            </div>
          </div>

          {props.mirror ? (
            <div className="containerPreviewButtonandZoom1">
              <div className="containerPreviewButton">
                <svg
                  className="replayPreviewIframe"
                  onClick={() => reloadIframe(refIframe)}
                  id="ico_play"
                  style={{
                    display: props.estatico ? "none" : "flex",
                    cursor: "pointer",
                    marginLeft: "0px",
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  width="38"
                  height="38"
                  viewBox="0 0 38 38"
                >
                  <defs>
                    <clipPath id="clipPath">
                      <rect
                        id="Retângulo_44"
                        data-name="Retângulo 44"
                        width="11.718"
                        height="17.411"
                        fill="#fff"
                      />
                    </clipPath>
                  </defs>
                  <circle
                    id="Elipse_24"
                    data-name="Elipse 24"
                    cx="19"
                    cy="19"
                    r="19"
                    fill="#6e4590"
                  />
                  <g
                    id="Grupo_113"
                    data-name="Grupo 113"
                    transform="translate(15.141 10.589)"
                    clipPath="url(#clipPath)"
                  >
                    <path
                      id="Caminho_152"
                      data-name="Caminho 152"
                      d="M0,1.93V15.481c0,1.717,2.288,2.576,3.626,1.363l7.47-6.776a1.8,1.8,0,0,0,0-2.724L3.626.568C2.288-.645,0,.214,0,1.93"
                      transform="translate(0 0)"
                      fill="#fff"
                    />
                  </g>
                </svg>
              </div>
              <div className="containerZoom">
                <svg
                  style={{ margin: "-4px 5px 0 0" }}
                  id="ico_lupa"
                  xmlns="http://www.w3.org/2000/svg"
                  xmlnsXlink="http://www.w3.org/1999/xlink"
                  width="16.87"
                  height="16.871"
                  viewBox="0 0 16.87 16.871"
                >
                  <defs>
                    <clipPath id="clip-path">
                      <rect
                        id="Retângulo_37"
                        dataname="Retângulo 37"
                        width="16.87"
                        height="16.871"
                        transform="translate(0 0)"
                        fill="#1b9ada"
                      />
                    </clipPath>
                  </defs>
                  <g
                    id="Grupo_95"
                    dataname="Grupo 95"
                    transform="translate(0 0)"
                    clipPath="url(#clip-path)"
                  >
                    <path
                      id="Caminho_155"
                      dataname="Caminho 155"
                      d="M1.658,16.586l4.19-4.192a6.9,6.9,0,1,0-1.371-1.372L.285,15.213a.971.971,0,0,0,1.373,1.373m4.823-13.2a4.95,4.95,0,1,1,0,7,4.956,4.956,0,0,1,0-7"
                      fill="#1b9ada"
                    />
                  </g>
                </svg>
                <Box sx={{ width: 120, margin: "10px 0 0 0" }}>
                  <input
                    type="range"
                    className="sliderRange"
                    id={`sliderstwo-${props.width}x${props.height}-${props.id}`}
                    name={`sliderstwo-${props.width}x${props.height}-${props.id}`}
                    defaultValue={getDefaultZoomValue}
                    min={10}
                    value={scaleInput}
                    onChange={(event) => changeZoom(event.target.value)}
                  />
                </Box>
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      )}
    </BannerDimensionsContext>
  );
};

export default Preview;
